import type { Mission } from "@/services/clientDataService";
import {
  isGroupMission,
  getSubMissions,
  areAllSubMissionsSelected,
} from "./subMissionUtils";

export interface GroupPriceResult {
  /** Prix total affiché (avec ou sans remise) */
  total: number;
  /** Prix total sans remise (somme brute des sous-missions sélectionnées) */
  fullTotal: number;
  /** La remise package est-elle active ? (toutes les sous-missions cochées) */
  hasDiscount: boolean;
  /** Pourcentage de remise (ex: 15) */
  discountPercent: number;
  /** Nombre de sous-missions sélectionnées */
  selectedCount: number;
  /** Nombre total de sous-missions */
  totalCount: number;
}

/**
 * Calcule le prix affiché pour une mission groupe selon la sélection actuelle.
 *
 * - Toutes les sous-missions sélectionnées → prix total - remise package
 * - Sélection partielle → somme des prix individuels (pas de remise)
 * - Aucune → 0
 */
export function calculateGroupDisplayPrice(
  parent: Mission,
  selectedIds: Set<string>,
  displayPriceById?: Record<string, number>
): GroupPriceResult {
  const subs = getSubMissions(parent);
  const discountPercent = parent.group_discount_percent ?? 15;

  if (subs.length === 0) {
    return {
      total: 0,
      fullTotal: 0,
      hasDiscount: false,
      discountPercent,
      selectedCount: 0,
      totalCount: 0,
    };
  }

  const allSelected = areAllSubMissionsSelected(parent, selectedIds);

  // Somme brute de TOUTES les sous-missions (pour le prix package)
  const allTotal = subs.reduce((sum, s) => {
    const price = displayPriceById?.[s.id] ?? s.price ?? 0;
    return sum + price;
  }, 0);

  // Somme des sous-missions sélectionnées uniquement
  const selectedTotal = subs.reduce((sum, s) => {
    if (!selectedIds.has(s.id)) return sum;
    const price = displayPriceById?.[s.id] ?? s.price ?? 0;
    return sum + price;
  }, 0);

  const selectedCount = subs.filter((s) => selectedIds.has(s.id)).length;

  if (allSelected) {
    // Remise package : -X% sur le total
    const discountedTotal = Math.round(allTotal * (1 - discountPercent / 100));
    return {
      total: discountedTotal,
      fullTotal: allTotal,
      hasDiscount: true,
      discountPercent,
      selectedCount,
      totalCount: subs.length,
    };
  }

  return {
    total: selectedTotal,
    fullTotal: allTotal,
    hasDiscount: false,
    discountPercent,
    selectedCount,
    totalCount: subs.length,
  };
}

// ---------------------------------------------------------------------------
// Pricing réel — remise groupe intégrée dans le calcul du subtotal
// ---------------------------------------------------------------------------

export interface GroupDiscountItem {
  /** Nom lisible du groupe parent */
  groupName: string;
  /** Pourcentage de remise appliqué */
  discountPercent: number;
  /** Montant de la remise en euros (positif) */
  amount: number;
}

/**
 * Détecte tous les groupes dont TOUTES les sous-missions sont sélectionnées
 * et calcule la remise correspondante.
 *
 * Utilisé par `calculatePricing` (useProjectPricing) pour intégrer la remise
 * groupe dans le subtotal réel, et par les appelants du moteur hybride.
 *
 * @param allMissions   - Missions top-level (incluant sub_missions[])
 * @param selectedMissionItems - Items missions sélectionnés avec leur quantity
 * @param priceResolver - Résout le prix d'une sous-mission (défaut: sub.price).
 *                        Permet de supporter le mode pack (days × tjm).
 */
export function computeGroupDiscounts(
  allMissions: Mission[],
  selectedMissionItems: Array<{ id: string; quantity: number }>,
  priceResolver?: (sub: Mission) => number,
): GroupDiscountItem[] {
  if (!Array.isArray(allMissions) || selectedMissionItems.length === 0) return [];

  const selectedIds = new Set(selectedMissionItems.map((i) => i.id));
  const qtyMap = new Map(selectedMissionItems.map((i) => [i.id, i.quantity]));
  const results: GroupDiscountItem[] = [];

  for (const mission of allMissions) {
    if (!isGroupMission(mission)) continue;
    const subs = getSubMissions(mission);
    if (subs.length === 0 || !areAllSubMissionsSelected(mission, selectedIds))
      continue;

    const discountPercent = Math.min(100, Math.max(0, mission.group_discount_percent ?? 15));
    const resolver = priceResolver ?? ((s: Mission) => s.price ?? 0);
    const fullTotal = subs.reduce(
      (sum, s) => sum + resolver(s) * (qtyMap.get(s.id) ?? 1),
      0,
    );
    const amount = Math.round(fullTotal * (discountPercent / 100));

    if (amount > 0) {
      results.push({ groupName: mission.title, discountPercent, amount });
    }
  }

  return results;
}
